import React, { useMemo } from 'react';
import { Card } from '../../CompositeComponents/Card/Card';
import { formatMoneyParts } from '../../../utils/currency';
import type { SalesWaveChartProps, SalesWaveDataPoint } from './types';

// ─── SalesWaveChart ───────────────────────────────────────────────────────────

const W = 400;
const H = 160;
const PAD = { top: 12, right: 16, bottom: 28, left: 44 };

function toPoints(data: SalesWaveDataPoint[], key: 'today' | 'yesterday', maxVal: number): string {
  if (!data.length) return '';
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  return data
    .map((d, i) => {
      const x = PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
      const y = PAD.top + innerH - (maxVal > 0 ? (d[key] / maxVal) * innerH : 0);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export function SalesWaveChart({
  data,
  title,
  currency = 'INR',
  valueFormat = 'money',
  className,
}: SalesWaveChartProps) {
  const fmt = (value: number): string =>
    valueFormat === 'money'
      ? formatMoneyParts(value, currency, { maximumFractionDigits: 0 })
      : value.toLocaleString('en-IN', { maximumFractionDigits: 1 });
  const maxVal = useMemo(
    () => Math.max(...data.map((d) => Math.max(d.today, d.yesterday)), 1),
    [data],
  );

  const todayPoints = useMemo(() => toPoints(data, 'today', maxVal), [data, maxVal]);
  const yestPoints = useMemo(() => toPoints(data, 'yesterday', maxVal), [data, maxVal]);

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  // Y-axis ticks (0, 50%, 100%)
  const yTicks = [0, 0.5, 1].map((pct) => ({
    y: PAD.top + innerH - pct * innerH,
    label: fmt(maxVal * pct),
  }));

  return (
    <Card className={className}>
      {title && (
        <p className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">{title}</p>
      )}

      {/* Legend */}
      <div className="flex gap-4 mb-2 text-xs text-neutral-500 dark:text-neutral-400">
        <span className="flex items-center gap-1">
          <svg width="20" height="4" aria-hidden="true">
            <line x1="0" y1="2" x2="20" y2="2" stroke="currentColor" strokeWidth="2" className="text-brand-500" />
          </svg>
          Today
        </span>
        <span className="flex items-center gap-1">
          <svg width="20" height="4" aria-hidden="true">
            <line x1="0" y1="2" x2="20" y2="2" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" className="text-neutral-400" />
          </svg>
          Yesterday
        </span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={title ?? 'Sales wave chart'}
      >
        {/* Grid lines */}
        {yTicks.map((t) => (
          <line
            key={t.y}
            x1={PAD.left}
            y1={t.y}
            x2={W - PAD.right}
            y2={t.y}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}

        {/* Y-axis labels */}
        {yTicks.map((t) => (
          <text
            key={t.y}
            x={PAD.left - 4}
            y={t.y + 4}
            textAnchor="end"
            fontSize="9"
            fill="#9ca3af"
          >
            {t.label}
          </text>
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => {
          const x = PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
          const show = data.length <= 8 || i % Math.ceil(data.length / 8) === 0;
          return show ? (
            <text
              key={i}
              x={x}
              y={H - 4}
              textAnchor="middle"
              fontSize="9"
              fill="#9ca3af"
            >
              {d.label}
            </text>
          ) : null;
        })}

        {/* Yesterday line (dashed neutral) */}
        {yestPoints && (
          <polyline
            points={yestPoints}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="1.5"
            strokeDasharray="5 3"
          />
        )}

        {/* Today line (solid brand) */}
        {todayPoints && (
          <polyline
            points={todayPoints}
            fill="none"
            stroke="#6366f1"
            strokeWidth="2"
          />
        )}

        {/* Today data points */}
        {data.map((d, i) => {
          const x = PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
          const y = PAD.top + innerH - (maxVal > 0 ? (d.today / maxVal) * innerH : 0);
          return (
            <circle key={i} cx={x} cy={y} r="3" fill="#6366f1">
              <title>{`${d.label} — Today: ${fmt(d.today)}`}</title>
            </circle>
          );
        })}
      </svg>
    </Card>
  );
}
